// Device element picker for the flow editor: snapshot a phone's UI, draw a box over every
// element, and hand the xpath of whatever the author taps back to the selected step.
//
// This is the "аля Appium" half of authoring — instead of typing an xpath, you tap the
// thing on screen. The same snapshot also answers "does this step still point at
// something?" for every node in the flow (green/red), which is how a locator broken by an
// app update is spotted before the flow is ever assigned to a team.
//
// One snapshot = one screen. A flow spans several screens, so nodes that belong to other
// screens read as "not here" rather than broken — the editor labels them accordingly.
//
// The snapshot itself comes from `/ui-dump`, which is adb's `uiautomator dump` — EXCEPT
// while a flow run holds a session on that phone, when the panel serves it from the run's
// own Appium driver instead (see common/flow_debug.py). The two cannot share a phone: adb's
// uiautomator and a live UiAutomator2 session fight, and the run is what dies.
//
// The screen is read when a phone is picked and then only on the Refresh button. A run
// used to re-read it after every stepped node; that was dropped — a snapshot per node
// through the run's own driver made stepping slow, and the author wants the picture when
// they ask for it, not a step later.
//
// It renders into a floating window the editor owns (see flow-editor-view), so the step
// list and its parameters stay usable while the phone's screen is on top of them.
import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { fetchUiDump } from './flows-shared';
import { openFlowDevicePicker } from './flow-device-picker';
import { mountLiveScreen } from './flow-live-screen';
import { refreshIconSvg } from '../stream/stream-controls-shared';
/** Parse `[x1,y1][x2,y2]`; null when malformed. */
function parseBounds(s) {
    const m = s.match(/\[(\d+),(\d+)\]\[(\d+),(\d+)\]/);
    return m ? [+m[1], +m[2], +m[3], +m[4]] : null;
}
/**
 * Rewrite an adb `uiautomator dump` hierarchy into the shape Appium reports.
 *
 * The two sources of a screen disagree on tag names: adb calls every element `<node
 * class="android.widget.TextView">`, Appium's `page_source` calls it
 * `<android.widget.TextView>`. That matters beyond parsing, because the xpath an author
 * records here is later evaluated BY APPIUM — so a candidate like
 * `//android.widget.TextView[@text='...']` is the one that will work at run time, while
 * counting its matches against the adb shape says "0 matches" and talks the author out of
 * it. Normalise once, and the inspector's numbers mean what the engine will do.
 */
function normalizeHierarchy(doc) {
    if (!doc.querySelector('node'))
        return doc;
    const out = document.implementation.createDocument(null, 'hierarchy', null);
    const copy = (src, dst) => {
        Array.from(src.children).forEach((child) => {
            const cls = child.getAttribute('class') || child.tagName;
            // Class names are dotted java identifiers; anything else (an inner class's `$`)
            // is not a legal XML name, and Appium spells those with a dot too.
            const tag = cls.replace(/[^A-Za-z0-9._-]/g, '.').replace(/^[^A-Za-z_]/, '_');
            const el = out.createElement(tag || 'node');
            Array.from(child.attributes).forEach((a) => el.setAttribute(a.name, a.value));
            dst.appendChild(el);
            copy(child, el);
        });
    };
    const root = doc.documentElement;
    Array.from(root.attributes).forEach((a) => out.documentElement.setAttribute(a.name, a.value));
    copy(root, out.documentElement);
    return out;
}
/** Flatten the uiautomator XML into pickable nodes, smallest first so a tap picks the tightest box. */
function collectNodes(doc) {
    const out = [];
    // Every element that has bounds, whatever it is called. The hierarchy arrives in one of
    // two shapes and they disagree on the tag name: `uiautomator dump` over adb names every
    // element `<node class="android.widget.TextView">`, while Appium's `page_source` — what
    // a live flow-debug session answers with — puts the class IN the tag
    // (`<android.widget.TextView class="...">`). Selecting `node` found nothing in the
    // second shape, so a refresh during a run drew a screenshot with no elements on it and
    // the inspector called the screen secure. `bounds` is what both actually share.
    doc.querySelectorAll('[bounds]').forEach((el) => {
        const b = parseBounds(el.getAttribute('bounds') ?? '');
        if (!b)
            return;
        const [x1, y1, x2, y2] = b;
        out.push({
            bounds: b,
            resourceId: el.getAttribute('resource-id') ?? '',
            contentDesc: el.getAttribute('content-desc') ?? '',
            text: el.getAttribute('text') ?? '',
            className: el.getAttribute('class') || el.tagName,
            clickable: el.getAttribute('clickable') === 'true',
            area: Math.max(1, (x2 - x1) * (y2 - y1)),
            el,
        });
    });
    return out.sort((a, b) => a.area - b.area);
}
/** Escape a value for use inside a single-quoted xpath literal. */
function xpLit(v) {
    return v.includes("'") ? `concat('${v.split("'").join("',\"'\",'")}')` : `'${v}'`;
}
/**
 * The element's absolute path, spelled the way Appium Inspector does: `/hierarchy/...`,
 * a `[k]` index only where same-named siblings make it ambiguous. It is the least stable
 * candidate (any layout change breaks it), but Compose screens often expose nothing
 * else, and it is the form the existing flows already carry.
 */
function absoluteXpath(el) {
    const parts = [];
    for (let cur = el; cur; cur = cur.parentElement) {
        const name = cur.tagName;
        const same = cur.parentElement
            ? Array.from(cur.parentElement.children).filter((c) => c.tagName === name)
            : [];
        parts.unshift(same.length > 1 ? `${name}[${same.indexOf(cur) + 1}]` : name);
    }
    return '/' + parts.join('/');
}
/** XPath candidates for a node, most stable first; the absolute path closes the list. */
function xpathCandidates(n) {
    const out = [];
    if (n.resourceId)
        out.push(`//*[@resource-id=${xpLit(n.resourceId)}]`);
    if (n.contentDesc)
        out.push(`//*[@content-desc=${xpLit(n.contentDesc)}]`);
    if (n.text)
        out.push(`//*[@text=${xpLit(n.text)}]`);
    if (n.className && n.resourceId)
        out.push(`//${n.className}[@resource-id=${xpLit(n.resourceId)}]`);
    if (n.className && n.text)
        out.push(`//${n.className}[@text=${xpLit(n.text)}]`);
    out.push(absoluteXpath(n.el));
    return out;
}
/**
 * Render the inspector into `host`. `onPick` receives the xpath the author chose for the
 * step that is currently selected in the editor.
 */
export function mountFlowInspector(host, opts) {
    // The phone is chosen through the farm → device picker, which is the only place that
    // knows every source (live map for host/aggregator, provider sync for duo/geelark).
    let device = null;
    let mode = 'control';
    let live = null;
    /** Whether the window around this is on screen — see setVisible. */
    let visible = false;
    // No device control here: the phone is picked in the run bar, which is the one
    // picker for authoring and running alike (see InspectorHandle.pick).
    host.innerHTML = `
        <div class="fi">
            <div class="fi__tabs" role="tablist">
                <button type="button" class="fi-tab active" data-mode="control">${esc(t('pf.mode_control'))}</button>
                <button type="button" class="fi-tab" data-mode="inspector">${esc(t('pf.mode_inspector'))}</button>
            </div>
            <div class="fi-live"></div>
            <div class="fi-stage" hidden><p class="settings-hint fi-hint">${esc(t('pf.dump_hint'))}</p></div>
            <div class="fi-element" hidden></div>
            <div class="settings-error modal__error fi-error" style="display:none"></div>
        </div>
    `;
    // Re-read the screen. In the window's header (opts.toolbar), shown only on the
    // Inspector tab — the Control tab is live and has nothing to refresh.
    const dumpBtn = document.createElement('button');
    dumpBtn.type = 'button';
    dumpBtn.className = 'fw__btn fi-dump';
    dumpBtn.disabled = true;
    dumpBtn.hidden = true;
    dumpBtn.innerHTML = `${refreshIconSvg}<span>${esc(t('pf.take_dump'))}</span>`;
    opts.toolbar.appendChild(dumpBtn);
    const liveEl = host.querySelector('.fi-live');
    const stage = host.querySelector('.fi-stage');
    const elementEl = host.querySelector('.fi-element');
    const errEl = host.querySelector('.fi-error');
    const showErr = (m) => { errEl.textContent = m; errEl.style.display = 'block'; };
    // Whoever cares that a phone was chosen (the run bar enables itself on it).
    const deviceListeners = [];
    function stopLive() {
        live?.stop();
        live = null;
    }
    /** (Re)connect the live screen for the current phone — only while it can be seen. */
    function startLive() {
        stopLive();
        if (!visible || mode !== 'control')
            return;
        live = mountLiveScreen(liveEl, device);
    }
    function setMode(next) {
        mode = next;
        host.querySelectorAll('.fi-tab')
            .forEach((b) => b.classList.toggle('active', b.dataset['mode'] === next));
        liveEl.hidden = next !== 'control';
        stage.hidden = next !== 'inspector';
        elementEl.hidden = next !== 'inspector';
        dumpBtn.hidden = next !== 'inspector';
        if (next === 'control') {
            if (!live)
                startLive();
        }
        else if (device && !doc) {
            // First look at the inspector for this phone: read the screen once so there is
            // something to tap. After that the button is the only thing that re-reads it.
            void takeDump();
        }
    }
    host.querySelectorAll('.fi-tab').forEach((b) => {
        b.addEventListener('click', () => setMode(b.dataset['mode']));
    });
    const pickDevice = () => {
        openFlowDevicePicker((picked) => {
            device = picked;
            doc = null;
            nodes = [];
            selected = null;
            stage.innerHTML = `<p class="settings-hint fi-hint">${esc(t('pf.dump_hint'))}</p>`;
            elementEl.innerHTML = '';
            dumpBtn.disabled = false;
            deviceListeners.forEach((cb) => cb());
            // A new phone: the live picture is its screen now; the snapshot is taken when
            // the inspector is first opened on it (setMode), or on the button.
            if (mode === 'control')
                startLive();
            else
                void takeDump();
        }, opts.anchor);
    };
    let doc = null;
    let nodes = [];
    let physW = 0, physH = 0;
    let selected = null;
    let dumping = false;
    function renderStage(png) {
        stage.innerHTML = `<div class="fi-imgbox"><img class="fi-img" alt="" src="data:image/png;base64,${png}"/><div class="fi-overlay"></div></div>`;
        const img = stage.querySelector('.fi-img');
        const overlay = stage.querySelector('.fi-overlay');
        // uiautomator reports bounds in PHYSICAL pixels while the screenshot is laid out at
        // whatever width the panel gives it, so every box is scaled by the rendered/physical
        // ratio — and redrawn on resize, or the boxes drift off their elements.
        const draw = () => {
            const sx = img.clientWidth / (physW || img.naturalWidth || 1);
            const sy = img.clientHeight / (physH || img.naturalHeight || 1);
            overlay.style.width = `${img.clientWidth}px`;
            overlay.style.height = `${img.clientHeight}px`;
            overlay.innerHTML = nodes
                .filter((n) => n.clickable || n.text || n.contentDesc || n.resourceId)
                .map((n) => {
                const [x1, y1, x2, y2] = n.bounds;
                const sel = selected && n.bounds.join() === selected.bounds.join() ? ' fi-box--sel' : '';
                return `<div class="fi-box${sel}" style="left:${x1 * sx}px;top:${y1 * sy}px;width:${(x2 - x1) * sx}px;height:${(y2 - y1) * sy}px"></div>`;
            })
                .join('');
        };
        if (img.complete)
            draw();
        else
            img.addEventListener('load', draw, { once: true });
        window.addEventListener('resize', draw);
        overlay.addEventListener('click', (e) => {
            const rect = overlay.getBoundingClientRect();
            const px = ((e.clientX - rect.left) / rect.width) * (physW || 1);
            const py = ((e.clientY - rect.top) / rect.height) * (physH || 1);
            // nodes are sorted smallest-first, so the first hit is the tightest box
            selected = nodes.find((n) => px >= n.bounds[0] && px <= n.bounds[2] && py >= n.bounds[1] && py <= n.bounds[3]) ?? null;
            draw();
            renderElement();
        });
    }
    function renderElement() {
        if (!selected) {
            elementEl.innerHTML = '';
            return;
        }
        const n = selected;
        // Every attribute is listed, empty ones as a dash — as Appium Inspector does, so
        // "no resource-id" is a fact on screen rather than a row that quietly went missing.
        const attrs = [
            ['resource-id', n.resourceId], ['content-desc', n.contentDesc],
            ['text', n.text], ['class', n.className],
            ['clickable', String(n.clickable)],
            ['bounds', `[${n.bounds[0]},${n.bounds[1]}][${n.bounds[2]},${n.bounds[3]}]`],
        ];
        elementEl.innerHTML = `
            <div class="fi-el">
                <div class="fi-el__h">${esc(t('pf.element'))}</div>
                ${attrs.map(([k, v]) => `<div class="fi-attr"><span>${esc(k)}</span><code${v ? '' : ' class="fi-attr__empty"'}>${esc(v || '—')}</code></div>`).join('')}
                <div class="fi-el__h">${esc(t('pf.xpath_candidates'))}</div>
                ${xpathCandidates(n).map((xp) => {
            const hits = matchCount(xp);
            const uniq = hits === 1 ? 'fi-cand--uniq' : '';
            return `<button type="button" class="fi-cand ${uniq}" data-xp="${esc(xp)}">
                        <code>${esc(xp)}</code><span class="fi-hits">${hits === 1 ? esc(t('pf.unique')) : esc(t('pf.n_matches', { n: String(hits) }))}</span>
                    </button>`;
        }).join('')}
                <p class="settings-hint">${esc(t('pf.candidate_hint'))}</p>
            </div>
        `;
        elementEl.querySelectorAll('.fi-cand').forEach((b) => {
            b.addEventListener('click', () => opts.onPick(b.dataset['xp'] ?? ''));
        });
    }
    function matchCount(xpath) {
        if (!doc)
            return -1;
        try {
            return doc.evaluate(xpath, doc, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE, null).snapshotLength;
        }
        catch {
            return -2;
        } // the xpath itself is malformed
    }
    async function takeDump() {
        errEl.style.display = 'none';
        if (!device) {
            showErr(t('pf.err_pick_device'));
            return;
        }
        // One snapshot at a time on a phone; the button is disabled meanwhile, this is
        // for the pick-then-click case that beats it.
        if (dumping)
            return;
        const udid = device.udid;
        dumping = true;
        dumpBtn.disabled = true;
        const label = dumpBtn.querySelector('span');
        label.textContent = t('pf.dumping');
        dumpBtn.classList.add('fi-dump--busy');
        try {
            const data = await fetchUiDump(udid);
            physW = data.physWidth;
            physH = data.physHeight;
            doc = new DOMParser().parseFromString(data.xml, 'application/xml');
            // A hierarchy the parser choked on yields zero nodes, which looks exactly like a
            // screen with nothing on it — say so instead of showing a picture you cannot tap.
            if (doc.querySelector('parsererror'))
                throw new Error(t('pf.err_dump_parse'));
            doc = normalizeHierarchy(doc);
            nodes = collectNodes(doc);
            if (!nodes.length)
                throw new Error(t('pf.err_dump_empty'));
            selected = null;
            renderStage(data.png);
            elementEl.innerHTML = '';
        }
        catch (e) {
            showErr(e.message);
        }
        finally {
            dumping = false;
            dumpBtn.disabled = false;
            dumpBtn.classList.remove('fi-dump--busy');
            label.textContent = t('pf.take_dump');
        }
    }
    dumpBtn.addEventListener('click', () => { void takeDump(); });
    setMode(mode);
    return {
        device() { return device; },
        onDevice(cb) { deviceListeners.push(cb); },
        pick() { pickDevice(); },
        setVisible(v) {
            visible = v;
            if (v) {
                if (!live)
                    startLive();
            }
            else
                stopLive();
        },
    };
}
